import clsx from "clsx";
import { FC, useMemo } from "react";

import type { Event, Score } from "@tsmono/inspect-common/types";
import {
  inferValueType,
  ScannerResultDetailView,
  ScanResultInput,
} from "@tsmono/scout-components/scanner-result-detail";
import {
  metadataWithoutScannerKeys,
  resolveScannerResultView,
} from "@tsmono/scout-components/sentinels";

import { useEvalSpec } from "../../../state/hooks";

import { SampleScannerPicker } from "./SampleScannerPicker";
import styles from "./SampleScansSidebar.module.css";
import { buildScanReferencePreviews } from "./scanReferencePreviews";
import { buildScoreMarkdownRefs, MakeCiteUrl } from "./scanReferences";

interface SampleScansSidebarProps {
  scores: Record<string, Score>;
  events?: readonly Event[] | null;
  makeCiteUrl: MakeCiteUrl;
  selected: string;
  onSelectedChange: (scanner: string) => void;
}

export const SampleScansSidebar: FC<SampleScansSidebarProps> = ({
  scores,
  events,
  makeCiteUrl,
  selected,
  onSelectedChange,
}) => {
  const scanners = Object.keys(scores);
  const score: Score | undefined = selected ? scores[selected] : undefined;

  const previewTable = useMemo(
    () => buildScanReferencePreviews(events ?? undefined),
    [events]
  );

  const references = useMemo(
    () =>
      buildScoreMarkdownRefs(
        score?.metadata ?? null,
        makeCiteUrl,
        previewTable
      ),
    [score?.metadata, makeCiteUrl, previewTable]
  );

  const viewer = useEvalSpec()?.viewer;
  const config = useMemo(
    () => resolveScannerResultView(viewer, selected),
    [viewer, selected]
  );

  if (!score) return null;

  const data: ScanResultInput = {
    value: score.value,
    valueType: inferValueType(score.value),
    answer: score.answer ?? undefined,
    explanation: score.explanation ?? undefined,
    metadata: metadataWithoutScannerKeys(score.metadata),
  };

  const header =
    scanners.length > 1 ? (
      <SampleScannerPicker
        scanners={scanners}
        selected={selected}
        onChange={onSelectedChange}
      />
    ) : scanners.length === 1 ? (
      <span className={clsx(styles.singleScanner, "text-size-smaller")}>
        {scanners[0]}
      </span>
    ) : null;

  return (
    <div aria-label="Sample scans">
      {header ? <div className={styles.header}>{header}</div> : null}
      <ScannerResultDetailView
        data={data}
        references={references}
        config={config}
      />
    </div>
  );
};
